import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../components/Layout';
import FuelPriceTable from '../components/FuelPriceTable';
import SEOContent from '../components/SEOContent';
import useTheme from '../hooks/useTheme';
import useExchangeRate from '../hooks/useExchangeRate';

const GallonsToLiters = () => {
  const { darkMode, toggleDarkMode, cardClass, inputClass } = useTheme();
  const { dollarRate } = useExchangeRate();

  const [gallons, setGallons] = useState('');
  const [result, setResult] = useState(null);

  const handleConvert = (value) => {
    setGallons(value);
    if (value && parseFloat(value) > 0) {
      const gallonsNum = parseFloat(value);
      const liters = gallonsNum * 3.78541;
      setResult({
        gallons: gallonsNum,
        liters: liters.toFixed(2),
        litersRounded: Math.round(liters * 10) / 10
      });
    } else {
      setResult(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Przelicznik galony na litry - Ile kosztuje galon paliwa w USA? | Kalkulator</title>
        <meta name="description" content="Przelicz galony na litry i sprawdź ile kosztuje galon benzyny w USA. Kalkulator konwersji galonów z tabelą cen paliwa w Teksasie i innych stanach USA 2025." />
        <meta name="keywords" content="przelicznik galony na litry, ile kosztuje galon paliwa w usa, galon cena, ile kosztuje galon benzyny w usa, 1 galon ile kosztuje, galon ile kosztuje w usa, ile litrów w galonie" />
        <link rel="canonical" href="https://galonyilitry.pl/przelicznik-galony-na-litry" />
      </Helmet>

      <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode} lang="pl">
        <div className="max-w-4xl mx-auto pt-20 pb-12 px-4">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 mb-4">
              Przelicznik galony na litry - ile kosztuje galon w USA?
            </h1>
            <p className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Szybko przelicz galony amerykańskie na litry i sprawdź cenę paliwa w USA. 1 galon = 3,78541 litra
            </p>
          </div>

          {/* Calculator Card */}
          <div className={`${cardClass} rounded-2xl border p-6 mb-8`}>
            <h2 className={`text-xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Kalkulator konwersji
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Input */}
              <div>
                <label className={`block ${darkMode ? 'text-slate-300' : 'text-slate-700'} mb-2 font-medium`}>
                  Galony (US):
                </label>
                <input
                  type="number"
                  className={`w-full px-4 py-3.5 border rounded-xl shadow-sm focus:outline-none focus:ring-2 transition-all input-green ${inputClass}`}
                  placeholder="Wpisz liczbę galonów"
                  value={gallons}
                  onChange={(e) => handleConvert(e.target.value)}
                />
              </div>

              {/* Output */}
              <div>
                <label className={`block ${darkMode ? 'text-slate-300' : 'text-slate-700'} mb-2 font-medium`}>
                  Litry:
                </label>
                <div className={`w-full px-4 py-3.5 border rounded-xl ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <span className={`text-xl font-bold ${darkMode ? 'text-cyan-400' : 'text-blue-600'}`}>
                    {result ? `${result.liters} L` : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Result Details */}
            {result && (
              <div className={`mt-6 p-4 rounded-xl ${darkMode ? 'bg-gradient-to-r from-blue-700 to-cyan-700' : 'bg-gradient-to-r from-blue-600 to-cyan-500'} text-white`}>
                <p className="text-center text-lg">
                  <span className="font-bold">{result.gallons}</span> galonów = <span className="font-bold">{result.liters}</span> litrów
                </p>
                <p className="text-center text-sm mt-1 opacity-80">
                  (w przybliżeniu {result.litersRounded} L)
                </p>
              </div>
            )}

            {/* Quick Reference */}
            <div className={`mt-6 grid grid-cols-2 md:grid-cols-4 gap-3`}>
              {[1, 5, 10, 20].map((gal) => (
                <button
                  key={gal}
                  onClick={() => handleConvert(gal.toString())}
                  className={`p-3 rounded-lg text-center transition-all ${
                    darkMode
                      ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                      : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                  }`}
                >
                  <div className="font-bold">{gal} gal</div>
                  <div className="text-sm opacity-80">{(gal * 3.78541).toFixed(1)} L</div>
                </button>
              ))}
            </div>
          </div>

          {/* Conversion Table */}
          <div className={`${cardClass} rounded-2xl border p-6 mb-8`}>
            <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Tabela przeliczeniowa galony na litry
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={darkMode ? 'bg-slate-700' : 'bg-blue-50'}>
                    <th className={`px-4 py-2 text-left ${darkMode ? 'text-cyan-300' : 'text-blue-900'}`}>Galony</th>
                    <th className={`px-4 py-2 text-right ${darkMode ? 'text-cyan-300' : 'text-blue-900'}`}>Litry</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 5, 10, 15, 20, 25, 30, 50].map((gal) => (
                    <tr key={gal} className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                      <td className={`px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{gal} gal</td>
                      <td className={`px-4 py-2 text-right font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {(gal * 3.78541).toFixed(2)} L
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Fuel Price Table */}
          <div className="mb-8">
            <FuelPriceTable darkMode={darkMode} dollarRate={dollarRate} lang="pl" />
          </div>

          {/* SEO Content */}
          <SEOContent darkMode={darkMode} title="Jak przeliczyć galony na litry?">
            <h3 className={`font-bold text-lg mb-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Wzór na przeliczanie galonów na litry
            </h3>
            <p>
              Przeliczanie galonów amerykańskich na litry jest bardzo proste. Wystarczy pomnożyć liczbę galonów przez współczynnik <strong>3,78541</strong>.
              Dla uproszczenia obliczeń w głowie możesz używać wartości 3,8 - błąd będzie minimalny.
            </p>
            <p className="mt-3">
              <strong>Wzór:</strong> Litry = Galony × 3,78541
            </p>

            <h3 className={`font-bold text-lg mb-2 mt-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Galon amerykański vs galon imperialny
            </h3>
            <p>
              Warto pamiętać, że galon amerykański (US gallon) różni się od galona imperialnego (UK gallon) używanego w Wielkiej Brytanii.
              Galon amerykański to 3,785 litra, natomiast galon imperialny to około 4,546 litra. W USA i podczas podróży po Stanach
              Zjednoczonych zawsze używany jest galon amerykański.
            </p>

            <h3 className={`font-bold text-lg mb-2 mt-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Praktyczne zastosowania
            </h3>
            <p>
              Przelicznik galonów na litry jest szczególnie przydatny podczas:
            </p>
            <ul className={`list-disc list-inside mt-2 space-y-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              <li>Planowania podróży samochodem po USA</li>
              <li>Tankowania samochodu w wypożyczalni</li>
              <li>Porównywania cen paliwa między USA a Polską</li>
              <li>Obliczania kosztów road tripu</li>
            </ul>
          </SEOContent>
        </div>
      </Layout>
    </>
  );
};

export default GallonsToLiters;
